import { useState } from "react"
import styled from "styled-components"

import { Skeleton, Text } from "@minuthq/meatball-ui-react"

import { GuestConversationNavItem } from "src/components/GuestInbox/GuestConversations/GuestConversationNavItem"
import { GuestConversationStatusSelect } from "src/components/GuestInbox/GuestConversations/GuestConversationStatusSelect"
import { areaPaddingSize } from "src/components/GuestInbox/guestInboxSharedStyles"
import { ScrollArea } from "src/components/ScrollArea/ScrollArea"
import { useFetchOrganizationGuestsInfinite } from "src/data/guests/queries/guestQueries"
import { TConversationStatus } from "src/data/guests/types/guestTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { spacing } from "src/ui/spacing"

export function GuestConversationsList() {
  const { tPlaceholder } = useTranslate()

  const { org } = useOrganization()

  const [conversationStatus, setConversationStatus] =
    useState<TConversationStatus>("unresolved")

  const fetchOrganizationGuests = useFetchOrganizationGuestsInfinite({
    orgId: org.id,
    filters: {
      conversation_status: conversationStatus,
      include_anonymized: false,
      sort_by: "last_message_created_at",
      sort_order: "desc",
      limit: 20,
    },
    options: {
      refetchInterval: 15000,
    },
  })

  if (fetchOrganizationGuests.isLoading) {
    return (
      <Container>
        <Header>
          <Text variant="heading3">{tPlaceholder("Inbox")}</Text>
        </Header>

        <ListWrapper>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton key={index} variant="rect" height={60} />
          ))}
        </ListWrapper>
      </Container>
    )
  }

  if (fetchOrganizationGuests.isError) {
    throw new Error("Could not fetch organization guests")
  }

  const guests = fetchOrganizationGuests.data.pages.flatMap(
    (page) => page.guests
  )

  return (
    <Container>
      <Header>
        <Text variant="heading3" marginBottom={spacing.S}>
          {tPlaceholder("Inbox")}
        </Text>

        <GuestConversationStatusSelect
          selectedValue={conversationStatus}
          onChange={setConversationStatus}
        />
      </Header>

      <ScrollArea
        onScrollToBottom={() => {
          fetchOrganizationGuests.fetchNextPage()
        }}
        scrollBottomThreshold={100}
      >
        <ListWrapper>
          {guests.length > 0 ? (
            guests.map((guest) => (
              <GuestConversationNavItem
                key={guest.guest_id}
                appHref={
                  Routes.InboxConversation.location(guest.guest_id).pathname
                }
                title={guest.name}
                summary={guest.guest_id}
                lastSentMessageAt={guest.conversation?.last_message_created_at}
              />
            ))
          ) : (
            <div>
              <Text variant="subtitle">{tPlaceholder("No conversations")}</Text>
            </div>
          )}

          {fetchOrganizationGuests.isFetchingNextPage && (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} variant="rect" height={30} />
              ))}
            </>
          )}
        </ListWrapper>
      </ScrollArea>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`

const Header = styled.div`
  padding: ${areaPaddingSize};
`

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.XS2};
  padding: 0 ${areaPaddingSize} ${areaPaddingSize} ${areaPaddingSize};
`
